import { ARCHIVE_REFUSAL } from './archive';

/**
 * Releasing a case to be shipped before it is paid. The lab's request of
 * 17 October 2026, item 8: "For the accountant add the option release. this is
 * for cases that are not paid but can be shipped, so the receptionist can ship
 * the case, and accountant can later track it."
 *
 * A release is a holder of pay's decision about money, as marking paid is, and
 * clears the case for dispatch as a payment does: once every kit is Ready it is
 * pending dispatch, and the dispatch desk sends it. It is still owed, so it
 * stays on To be paid, and on Released, until it is paid. A hold outranks it as
 * it outranks a payment. Taken back by Undo, or later from Released, while the
 * case is still in the lab; once it has left there is nothing to take back.
 *
 * The API's ReleaseRules, in the same words.
 */

/** When the case was released, and who did it. */
export interface CaseRelease {
  at: string;
  byName: string | null;
}

export const RELEASE_REFUSAL = {
  noPermission: 'You do not have permission to release a case.',
  alreadyPaid: 'This case is paid. A paid case needs no release to be shipped.',
  alreadyReleased: 'This case is already released for shipping.',
  dispatched: 'This case has left the lab.',
  notReleased: 'This case is not released.',
  leftAlready: 'This case has left the lab, so its release cannot be taken back.',
} as const;

/**
 * The words for a case refused by one of the money actions on many cases at
 * once: item 9 of the same request. The API's BulkMoney.
 */
export const BULK_MONEY_REFUSAL = {
  noPayPermission: 'You do not have permission to take payments.',
  alreadyPaid: 'This case is already paid.',
  notPaid: 'This case is not paid.',
} as const;

/** What a release is decided by, as a job order carries it. */
export interface Releasable {
  payment: { paid: boolean };
  /** Absent from an API older than releases, which is none. */
  release?: CaseRelease | null;
  dispatch: { dispatched: boolean };
  archive: { archived: boolean };
}

export function isReleased(order: Pick<Releasable, 'release'>): boolean {
  return order.release != null;
}

/** Paid, or released: what lets a finished case be dispatched. */
export function isCleared(order: Pick<Releasable, 'payment' | 'release'>): boolean {
  return order.payment.paid || isReleased(order);
}

/** Why a case cannot be released now, or null when it can. Gone first, then archived: the two nobody at Payments can change. */
export function releaseRefusal(order: Releasable): string | null {
  if (order.dispatch.dispatched) return RELEASE_REFUSAL.dispatched;
  if (order.archive.archived) return ARCHIVE_REFUSAL.isArchived;
  if (order.payment.paid) return RELEASE_REFUSAL.alreadyPaid;
  return isReleased(order) ? RELEASE_REFUSAL.alreadyReleased : null;
}

/** Why a case's release cannot be taken back now, or null when it can. A case paid since may still have it taken back. */
export function takeBackRefusal(order: Pick<Releasable, 'release' | 'dispatch'>): string | null {
  if (!isReleased(order)) return RELEASE_REFUSAL.notReleased;
  return order.dispatch.dispatched ? RELEASE_REFUSAL.leftAlready : null;
}
